import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePage } from '@inertiajs/react';

import {
    emitLayoutPreferencesChange,
    readLayoutPreferences,
    storageKey,
    subscribeLayoutPreferences,
    writeLayoutPreferences,
    type LayoutPreferences,
    type NexusLayoutMode,
    type NexusThemeMode,
    type TableActionsDisplayMode,
} from './layoutPreferencesStorage';
import type { PageProps } from '../types';

export type { LayoutPreferences, NexusLayoutMode, NexusThemeMode, TableActionsDisplayMode };

/**
 * Preferência de layout Nexus (menu lateral × menu superior, tema e estilo dos botões de ação).
 * Persistida em localStorage por usuário (`user_logged.id`).
 * Preferências de colunas de tabelas ficam no banco (API Nexus).
 *
 * Comportamento mobile: mesmo com `mode: 'top'`, o shell continua usando
 * drawer (sidebar off-canvas) — o menu horizontal só aparece no desktop.
 */
export function useLayoutPreferences() {
    const { props } = usePage<PageProps>();
    const userId = props.auth?.user?.id ?? null;
    const key = useMemo(() => storageKey(userId), [userId]);

    const [preferences, setPreferences] = useState<LayoutPreferences>(() =>
        readLayoutPreferences(key),
    );

    useEffect(() => {
        setPreferences(readLayoutPreferences(key));
    }, [key]);

    useEffect(() => {
        const refresh = () => setPreferences(readLayoutPreferences(key));
        const unsubscribe = subscribeLayoutPreferences(refresh);

        const onStorage = (event: StorageEvent) => {
            if (event.key === key) {
                refresh();
            }
        };
        window.addEventListener('storage', onStorage);

        return () => {
            unsubscribe();
            window.removeEventListener('storage', onStorage);
        };
    }, [key]);

    const setMode = useCallback(
        (mode: NexusLayoutMode) => {
            const current = readLayoutPreferences(key);
            const next: LayoutPreferences = { ...current, mode };
            writeLayoutPreferences(key, next);
            setPreferences(next);
            emitLayoutPreferencesChange();
        },
        [key],
    );

    const setTheme = useCallback(
        (theme: NexusThemeMode) => {
            const current = readLayoutPreferences(key);
            const next: LayoutPreferences = { ...current, theme };
            writeLayoutPreferences(key, next);
            setPreferences(next);
            emitLayoutPreferencesChange();
        },
        [key],
    );

    const setTableActionsMode = useCallback(
        (tableActionsMode: TableActionsDisplayMode) => {
            const current = readLayoutPreferences(key);
            const next: LayoutPreferences = { ...current, tableActionsMode };
            writeLayoutPreferences(key, next);
            setPreferences(next);
            emitLayoutPreferencesChange();
        },
        [key],
    );

    const setTopNavOrder = useCallback(
        (topNavOrder: string[]) => {
            const current = readLayoutPreferences(key);
            const next: LayoutPreferences = { ...current, topNavOrder };
            writeLayoutPreferences(key, next);
            setPreferences(next);
            emitLayoutPreferencesChange();
        },
        [key],
    );

    const resetTopNavOrder = useCallback(() => {
        const current = readLayoutPreferences(key);
        const { topNavOrder: _removed, ...rest } = current;
        const next: LayoutPreferences = { ...rest, topNavOrder: undefined };
        writeLayoutPreferences(key, next);
        setPreferences(next);
        emitLayoutPreferencesChange();
    }, [key]);

    return {
        mode: preferences.mode,
        theme: preferences.theme ?? 'light',
        topNavOrder: preferences.topNavOrder,
        tableActionsMode: preferences.tableActionsMode ?? 'buttons',
        preferences,
        setMode,
        setTheme,
        setTableActionsMode,
        setTopNavOrder,
        resetTopNavOrder,
        /** true quando o shell deve usar navegação superior no desktop. */
        isTopNav: preferences.mode === 'top',
    };
}
